import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const JPEG_QUALITY = 0.85;

function isAppleTouchDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iP(hone|ad|od)/.test(ua) ||
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints || 0) > 1)
  );
}

function renderLimits() {
  const lowMemory =
    isAppleTouchDevice() ||
    (typeof navigator !== "undefined" &&
      typeof navigator.deviceMemory === "number" &&
      navigator.deviceMemory <= 4);
  return { scale: 1.5, maxEdge: lowMemory ? 1024 : 1280 };
}

function asUint8Array(bytes) {
  if (bytes instanceof Uint8Array) return bytes;
  return new Uint8Array(bytes);
}

function isRecoverablePdfParseError(error) {
  return String(error?.message || "").includes("Failed to parse");
}

function canvasToJpegBytes(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      async (blob) => {
        if (!blob) {
          reject(new Error("Could not rebuild a PDF page for editing."));
          return;
        }
        resolve(new Uint8Array(await blob.arrayBuffer()));
      },
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

async function rebuildPdfWithPdfJs(data) {
  const { scale: preferredScale, maxEdge } = renderLimits();
  const pdf = await pdfjs.getDocument({ data: data.slice(0) }).promise;
  const out = await PDFDocument.create();

  try {
    for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const unit = page.getViewport({ scale: 1 });
      const longest = Math.max(unit.width, unit.height) || 1;
      const scale = Math.max(0.25, Math.min(preferredScale, maxEdge / longest));
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width));
      canvas.height = Math.max(1, Math.floor(viewport.height));
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      await page.render({
        canvasContext: ctx,
        viewport,
        background: "rgb(255,255,255)",
      }).promise;

      const jpg = await canvasToJpegBytes(canvas);
      const image = await out.embedJpg(jpg);
      const pdfPage = out.addPage([unit.width, unit.height]);
      pdfPage.drawImage(image, {
        x: 0,
        y: 0,
        width: unit.width,
        height: unit.height,
      });
      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();
    }
  } finally {
    try {
      await pdf.destroy();
    } catch {
      pdf.cleanup?.();
    }
  }

  return out.save({ useObjectStreams: false });
}

async function loadRepairedPdf(data) {
  const bytes = await rebuildPdfWithPdfJs(data);
  return PDFDocument.load(bytes, { ignoreEncryption: true });
}

export async function loadPdfForEdit(bytes) {
  const data = asUint8Array(bytes);
  try {
    const doc = await PDFDocument.load(data, { ignoreEncryption: true });
    return doc;
  } catch (error) {
    if (!isRecoverablePdfParseError(error)) throw error;
    try {
      return await loadRepairedPdf(data);
    } catch (rebuildError) {
      const failed = new Error(
        rebuildError?.message ||
          "This PDF could not be edited. Open it, re-save a copy, and try again."
      );
      failed.cause = error;
      throw failed;
    }
  }
}
